import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
    FaTimes, 
    FaCheck, 
    FaSearchPlus, 
    FaSearchMinus, 
    FaRedo, 
    FaUndo, 
    FaArrowsAlt,
    FaCropAlt,
    FaSyncAlt
} from 'react-icons/fa';
import './ImageCropperModal.css';

const CROP_BOX_SIZE = 280; // Size of the crop circle in px on UI
const EXPORT_SIZE = 512;    // Final output resolution (512x512)

const ImageCropperModal = ({ isOpen, imageSrc, onCropComplete, onCancel, fileName = 'avatar.jpg' }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const imgRef = useRef(null);
    const dragStartRef = useRef({ x: 0, y: 0, startOffsetX: 0, startOffsetY: 0 });
    const containerRef = useRef(null);

    // Reset when modal opens with a new image
    useEffect(() => {
        if (isOpen && imageSrc) {
            setZoom(1);
            setRotation(0);
            setOffset({ x: 0, y: 0 });
            setImageLoaded(false);

            const img = new Image();
            img.onload = () => {
                setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                setImageLoaded(true);
            };
            img.src = imageSrc;
        }
    }, [isOpen, imageSrc]);

    // Calculate effective dimensions based on rotation
    const isRotated90or270 = rotation === 90 || rotation === 270;
    const effectiveWidth = isRotated90or270 ? imageDimensions.height : imageDimensions.width;
    const effectiveHeight = isRotated90or270 ? imageDimensions.width : imageDimensions.height;

    // Calculate base scale so image always covers the crop box (cover fit)
    const baseScale = effectiveWidth && effectiveHeight 
        ? Math.max(CROP_BOX_SIZE / effectiveWidth, CROP_BOX_SIZE / effectiveHeight)
        : 1;

    const currentScale = baseScale * zoom;

    // Rendered dimensions of the image
    const renderedWidth = imageDimensions.width * currentScale;
    const renderedHeight = imageDimensions.height * currentScale;

    // Effective rendered bounds after rotation for pan clamping
    const renderedEffWidth = effectiveWidth * currentScale;
    const renderedEffHeight = effectiveHeight * currentScale;

    const maxOffsetX = Math.max(0, (renderedEffWidth - CROP_BOX_SIZE) / 2);
    const maxOffsetY = Math.max(0, (renderedEffHeight - CROP_BOX_SIZE) / 2);

    // Clamp offset helper
    const clampOffset = useCallback((x, y, maxOffX, maxOffY) => {
        return {
            x: Math.max(-maxOffX, Math.min(maxOffX, x)),
            y: Math.max(-maxOffY, Math.min(maxOffY, y))
        };
    }, []);

    // Pointer down (start drag)
    const handlePointerDown = (e) => {
        if (!imageLoaded) return;
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startOffsetX: offset.x,
            startOffsetY: offset.y
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    // Pointer move (dragging)
    const handlePointerMove = (e) => {
        if (!isDragging) return;
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;

        const newX = dragStartRef.current.startOffsetX + deltaX;
        const newY = dragStartRef.current.startOffsetY + deltaY;

        setOffset(clampOffset(newX, newY, maxOffsetX, maxOffsetY));
    };

    // Pointer up
    const handlePointerUp = (e) => {
        if (isDragging) {
            setIsDragging(false);
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
    };

    // Wheel zoom
    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.1 : -0.1;
        setZoom((prev) => {
            const nextZoom = Math.min(3.5, Math.max(1, +(prev + delta).toFixed(2)));
            return nextZoom;
        });
    };

    // Zoom buttons
    const handleZoomIn = () => {
        setZoom((prev) => Math.min(3.5, +(prev + 0.2).toFixed(2)));
    };

    const handleZoomOut = () => {
        setZoom((prev) => {
            const next = Math.max(1, +(prev - 0.2).toFixed(2));
            return next;
        });
    };

    // Rotation
    const handleRotateClockwise = () => {
        setRotation((prev) => (prev + 90) % 360);
        setOffset({ x: 0, y: 0 }); // reset pan on rotation for best fit
    };

    const handleRotateCounterClockwise = () => {
        setRotation((prev) => (prev - 90 + 360) % 360);
        setOffset({ x: 0, y: 0 });
    };

    // Reset view
    const handleReset = () => {
        setZoom(1);
        setRotation(0);
        setOffset({ x: 0, y: 0 });
    };

    // Confirm crop & render to Canvas
    const handleCrop = () => {
        if (!imageLoaded || !imgRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = EXPORT_SIZE;
        canvas.height = EXPORT_SIZE;
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        // Ratio between export resolution and UI crop circle
        const ratio = EXPORT_SIZE / CROP_BOX_SIZE;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Clear canvas
        ctx.clearRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);

        ctx.save();
        // Move origin to center of canvas
        ctx.translate(EXPORT_SIZE / 2, EXPORT_SIZE / 2);

        // Apply pan offset scaled by ratio
        ctx.translate(offset.x * ratio, offset.y * ratio);

        // Apply rotation
        ctx.rotate((rotation * Math.PI) / 180);

        // Draw image centered at the origin
        const exportWidth = renderedWidth * ratio;
        const exportHeight = renderedHeight * ratio;

        ctx.drawImage(
            imgRef.current,
            -exportWidth / 2,
            -exportHeight / 2,
            exportWidth,
            exportHeight
        );

        ctx.restore();

        // Convert canvas to Blob / File
        canvas.toBlob((blob) => {
            if (!blob) return;
            const fileExt = fileName.toLowerCase().endsWith('.png') ? 'png' : 'jpg';
            const mimeType = fileExt === 'png' ? 'image/png' : 'image/jpeg';
            const croppedFile = new File([blob], fileName || `cropped_avatar.${fileExt}`, {
                type: mimeType,
                lastModified: Date.now()
            });

            const previewUrl = canvas.toDataURL(mimeType, 0.92);
            onCropComplete(croppedFile, previewUrl);
        }, 'image/jpeg', 0.92);
    };

    if (!isOpen || !imageSrc) return null;

    return (
        <div className="cropper-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
            <div className="cropper-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="cropper-header">
                    <div className="cropper-title">
                        <FaCropAlt className="cropper-title-icon" />
                        <div>
                            <h3>Ajustar Foto de Perfil</h3>
                            <p>Posicione, rode e amplie a imagem para o enquadramento ideal.</p>
                        </div>
                    </div>
                    <button className="cropper-close-btn" onClick={onCancel} title="Fechar">
                        <FaTimes />
                    </button>
                </div>

                {/* Crop Stage */}
                <div className="cropper-stage-wrapper">
                    <div 
                        ref={containerRef}
                        className={`cropper-viewport ${isDragging ? 'is-dragging' : ''}`}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        onWheel={handleWheel}
                    >
                        {/* Hidden source image to draw from */}
                        <img
                            ref={imgRef}
                            src={imageSrc}
                            alt="Original"
                            className="cropper-hidden-img"
                            crossOrigin="anonymous"
                        />

                        {/* Visual preview image transformed */}
                        <div 
                            className="cropper-transform-layer"
                            style={{
                                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) rotate(${rotation}deg)`,
                                width: `${renderedWidth}px`,
                                height: `${renderedHeight}px`
                            }}
                        >
                            <img
                                src={imageSrc}
                                alt="Crop Preview"
                                className="cropper-display-img"
                                draggable={false}
                            />
                        </div>

                        {/* Circular Mask Overlay */}
                        <div className="cropper-mask-circle">
                            <div className="cropper-grid">
                                <div className="cropper-grid-line cropper-grid-h1" />
                                <div className="cropper-grid-line cropper-grid-h2" />
                                <div className="cropper-grid-line cropper-grid-v1" />
                                <div className="cropper-grid-line cropper-grid-v2" />
                            </div>
                        </div>

                        {/* Hint overlay */}
                        <div className="cropper-hint">
                            <FaArrowsAlt /> Arrasta para reposicionar
                        </div>
                    </div>
                </div>

                {/* Controls Bar */}
                <div className="cropper-controls">
                    {/* Zoom Section */}
                    <div className="cropper-control-row">
                        <span className="cropper-control-label">Zoom:</span>
                        <button 
                            type="button" 
                            className="cropper-tool-btn" 
                            onClick={handleZoomOut}
                            disabled={zoom <= 1}
                            title="Diminuir Zoom"
                        >
                            <FaSearchMinus />
                        </button>
                        <input
                            type="range"
                            min="1"
                            max="3.5"
                            step="0.05"
                            value={zoom}
                            onChange={(e) => setZoom(parseFloat(e.target.value))}
                            className="cropper-slider"
                        />
                        <button 
                            type="button" 
                            className="cropper-tool-btn" 
                            onClick={handleZoomIn}
                            disabled={zoom >= 3.5}
                            title="Aumentar Zoom"
                        >
                            <FaSearchPlus />
                        </button>
                        <span className="cropper-zoom-badge">{Math.round(zoom * 100)}%</span>
                    </div>

                    {/* Rotation & Reset Row */}
                    <div className="cropper-secondary-controls">
                        <div className="cropper-tools-group">
                            <button
                                type="button"
                                className="cropper-btn-pill"
                                onClick={handleRotateCounterClockwise}
                                title="Rodar 90º para a esquerda"
                            >
                                <FaUndo /> -90°
                            </button>
                            <button
                                type="button"
                                className="cropper-btn-pill"
                                onClick={handleRotateClockwise}
                                title="Rodar 90º para a direita"
                            >
                                <FaRedo /> +90°
                            </button>
                            <button
                                type="button"
                                className="cropper-btn-pill cropper-btn-reset"
                                onClick={handleReset}
                                title="Restaurar posição original"
                            >
                                <FaSyncAlt /> Centrar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="cropper-footer">
                    <button type="button" className="cropper-btn-cancel" onClick={onCancel}>
                        Cancelar
                    </button>
                    <button type="button" className="cropper-btn-apply" onClick={handleCrop}>
                        <FaCheck /> Recortar e Aplicar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropperModal;
